import { Add, ChevronRight, DeleteOutline, EditOutlined, Folder, InfoOutlined, InsertDriveFile, Recycling, SouthWest } from "@mui/icons-material";
import { Alert, Box, DialogContent, DialogTitle, Fab, Grid2 as Grid, ListItemIcon, ListItemText, Menu, MenuItem, Skeleton, Stack, styled, Table, TableBody, TableCell, tableCellClasses, TableContainer, TableRow, Typography, useMediaQuery, useTheme } from "@mui/material";
import { SimpleTreeView, TreeItem } from "@mui/x-tree-view";
import copy from "fast-copy";
import moment from 'moment';
import { enqueueSnackbar } from "notistack";
import React from "react";
import { LinkFilter } from "../../interfaces";
import { useAppDispatch } from "../../redux";
import { useDeleteFolderMutation, useImportFromFolderTemplateMutation, useLazyGetFoldersQuery } from "../../redux/services";
import { closeBackdrop, openBackdrop } from "../../redux/slices/backdrop";
import { closeConfirmationDialog, openConfirmationDialog, updateConfirmationDialogPositiveBtn, updateCongirmationDialogBody } from "../../redux/slices/confirmationDialog";
import { closeDialog, openDialog } from "../../redux/slices/dialog";
import { setOpenWebSideNavBar } from "../../redux/slices/utils";
import { ROOT_FOLDER_NAME } from "../../utils";
import { Documents } from "../documents";
import { MyPaper } from "../myPaper";
import { AddEditFolder } from "./addFolder";
import { ImportFromFolderTemplate } from "./importFromFolderTemplate";

export type ObjectType = 'material' | 'bom' | 'project' | 'product' | 'proposal' | 'quote' | 'vendor' | 'instantSearch';

export type FolderDetails = { object_type: string, object_id: string };

interface iDocumentV2Prop {
    object_id?: string,
    object_type?: ObjectType,
    object_sequence_id?: string,
    object_association?: any,
    updateCurrentPath?: (folderName: string) => void,
    updateHistory?: (history: { label: string, value: string }[], currentPath: string) => void,
    onFolderSelected?: (folder: folderState, parentFolder?: folderState) => void,
    creatable: boolean,
    updatable: boolean,
    publishable: boolean,
    deletable: boolean,
    readable: boolean,
    unlinkDoc?: (id: string, refetch: any, row: any) => void,
    hasCheckbox?: boolean,
    linkDocument?: (links: any, refetch: any, unlinks: string[], object_type: string, folderDetails: FolderDetails) => void,
    isPdfOnly?: boolean,
    showHelperBtn?: boolean,
    showCreateBtn?: boolean,
    showDeleteBtn?: boolean,
    parentData?: LinkFilter,
    sequence_id?: string,
    currentItem?: folderState,
    hideDocuments?: boolean,
    hideShadow?: boolean,
    onRegenerate?: any,
    onDocumentUploadComplete?: any,
    isCommonModule?: boolean
}

export interface folderState {
    _id: string,
    index: number[],
    folder_name: string,
    sequence_id?: string,
    version?: number,
    description?: string,
    created_time: string,
    created_user: string,
    modified_time?: string,
    modified_user?: string,
    system_defined: boolean,
    folders: folderState[],
    ancestors: folderState[]
}

export interface historyInterface { label: string, value: string }

const trash = {
    _id: "trash",
    index: [0],
    folders: [],
    folder_name: "Trash",
    system_defined: false,
    created_time: new Date().toISOString(),
    created_user: "system",
    ancestors: []
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: 14,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

export const FolderProperties = (details: folderState) => {
    return (
        <>
            <DialogTitle>
                <Typography
                    variant="h6"
                    color="textPrimary"
                    sx={{
                        width: "100%",
                        fontFamily: 'htrts_medium',
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                    }}
                >
                    Properties of {`${details?.folder_name}`}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <TableContainer>
                    <Table size="small">
                        <TableBody>
                            <StyledTableRow>
                                <StyledTableCell sx={{ minWidth: 150 }}>
                                    <Typography variant="subtitle2" color="textSecondary">Folder ID</Typography>
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Typography variant="body2">{details?.sequence_id || "-"}</Typography>
                                </StyledTableCell>
                            </StyledTableRow>

                            <StyledTableRow>
                                <StyledTableCell sx={{ minWidth: 150 }}>
                                    <Typography variant="subtitle2" color="textSecondary">Description</Typography>
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Typography variant="body2">{details?.description || "-"}</Typography>
                                </StyledTableCell>
                            </StyledTableRow>

                            <StyledTableRow>
                                <StyledTableCell>
                                    <Typography variant="subtitle2" color="textSecondary">Created Time</Typography>
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Typography variant="body2">{moment(details?.created_time).format('lll')}</Typography>
                                </StyledTableCell>
                            </StyledTableRow>

                            <StyledTableRow>
                                <StyledTableCell>
                                    <Typography variant="subtitle2" color="textSecondary">Created User</Typography>
                                </StyledTableCell>
                                <StyledTableCell>
                                    <Typography variant="body2">{details?.created_user}</Typography>
                                </StyledTableCell>
                            </StyledTableRow>

                            {details.modified_time && <>
                                <StyledTableRow>
                                    <StyledTableCell>
                                        <Typography variant="subtitle2" color="textSecondary">Modified Time</Typography>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Typography variant="body2">{moment(details?.modified_time).format('lll')}</Typography>
                                    </StyledTableCell>
                                </StyledTableRow>

                                <StyledTableRow>
                                    <StyledTableCell>
                                        <Typography variant="subtitle2" color="textSecondary">Modified User</Typography>
                                    </StyledTableCell>
                                    <StyledTableCell>
                                        <Typography variant="body2">{details?.modified_user}</Typography>
                                    </StyledTableCell>
                                </StyledTableRow>
                            </>}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
        </>
    );
};

export const TreeItemLoader: React.FC = () => <Stack gap={1}>
    {[1, 2, 3, 4].map(key => <Skeleton key={key} variant="rounded" height={24} />)}
</Stack>;

export const Folders: React.FC<iDocumentV2Prop> = ({
    object_id, object_type, object_sequence_id, object_association, updateCurrentPath, updateHistory, onFolderSelected,
    creatable, updatable, deletable, readable, publishable, unlinkDoc, hasCheckbox, linkDocument, showHelperBtn = false, showCreateBtn = true,
    showDeleteBtn = true, parentData, isPdfOnly, sequence_id, hideDocuments = false, isCommonModule = false, ...props
}) => {

    const dispatch = useAppDispatch();

    const theme = useTheme();

    const isMobileMatch = useMediaQuery(theme.breakpoints.down("sm"));

    const [fetchFolders, foldersData] = useLazyGetFoldersQuery();
    const [deleteFolder] = useDeleteFolderMutation();
    const [importFromFolderMutation] = useImportFromFolderTemplateMutation();

    const [folders, setFolders] = React.useState<folderState[]>(props.currentItem ? [{ ...props.currentItem, index: [0], folders: [] }] : []);
    const [expanded, setExpanded] = React.useState<string[]>([]);
    const [selected, setSelected] = React.useState<string>('');
    const [currentItem, setCurrentItem] = React.useState<folderState | null>(props.currentItem ?? null);
    const [parentItem, setParentItem] = React.useState<folderState | undefined>();
    const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | HTMLLIElement | null>(null);
    const [anchorElForFolderActions, setFolderActions] = React.useState<HTMLDivElement | HTMLLIElement | null>(null);
    const menuRef = React.useRef<HTMLDivElement | null>(null)
    const [history, setHistory] = React.useState<historyInterface[]>([]);

    const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.MouseEvent<HTMLLIElement, MouseEvent>) => {
        event.currentTarget.click();
        event.preventDefault();

        if (selected === currentItem?._id) {
            setAnchorEl(event.currentTarget);
        } else {
            setAnchorEl(null);
        }
    };

    const handleFolderClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent> | React.MouseEvent<HTMLLIElement, MouseEvent>) => {
        event.currentTarget.click();
        event.preventDefault();


        if (selected === currentItem?._id) {
            setFolderActions(event.currentTarget);
        } else {
            setFolderActions(null);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleFolderActionsClose = () => {
        setFolderActions(null);
    };

    const open = Boolean(anchorEl);
    const id = open ? 'treeItem-' + currentItem?._id : undefined;

    const openFolderActions = Boolean(anchorElForFolderActions);
    const folderActionsId = openFolderActions ? 'folder-root-actions-' + currentItem?._id : undefined;

    const handleToggle = (event: React.SyntheticEvent, nodeIds: string[]) => setExpanded(nodeIds);

    const handleSelect = (nodeIds: string) => setSelected(nodeIds);

    const onFolderClicked = (folder: folderState, parentFolder?: folderState) => {
        setCurrentItem(folder);
        setParentItem(parentFolder);
        document.getElementsByClassName(`${folder?._id}`)?.[0]?.scrollIntoView({ behavior: "smooth", block: "center", inline: "start" });

        fetchFolderById({ folderId: folder._id !== 'root' ? folder._id : undefined, index: folder?.index ?? [] });

        if (folder.folders?.length === 0) {
            setExpanded((oldExpanded) => [...oldExpanded, folder._id]);
        }

        onFolderSelected?.(folder, parentFolder);
        buildAndUpdateHistory(folder);
    };

    const buildAndUpdateHistory = (folder: folderState) => {
        let newHistory: { label: string, value: string }[] = [{ value: 'documents', label: 'Documents' }];
        let parentFolder: folderState = folders[folder.index[0]];

        for (let i = 1; i < folder.index.length; i++) {
            const folder_name = parentFolder?.folder_name === "root" && parentFolder?.sequence_id === "F-000001" ? ROOT_FOLDER_NAME : parentFolder?.folder_name;
            newHistory.push({ label: folder_name, value: parentFolder._id })
            if (parentFolder.folders) {
                parentFolder = parentFolder.folders[folder.index[i]];
            }
        }

        const folder_name = folder?.sequence_id === "F-000001" ? ROOT_FOLDER_NAME : folder?.folder_name;

        if (updateHistory) {
            updateHistory(newHistory, folder_name);
        };

        setHistory(newHistory);
    };

    const giveMeTreeItems = (folder: folderState, parentFolder?: folderState) => {
        const folder_name = folder?.sequence_id === "F-000001" ? ROOT_FOLDER_NAME : folder?.folder_name ?? "";
        const isRootFolder = folder?.sequence_id === "F-000001" || folder?._id === "root";
        const RootFolder = () => <FolderIcon {...folder} />;
        return <TreeItem
            slots={isRootFolder ? { expandIcon: RootFolder, collapseIcon: RootFolder, endIcon: RootFolder } : undefined}
            itemId={folder._id}
            label={
                <Stack onContextMenu={handleClick} direction={"row"} gap={1} alignItems={"center"} sx={{ width: "100%" }}>
                    {isRootFolder ? <></> : <FolderIcon {...folder} />}
                    <Box>
                        <Typography sx={{ wordBreak: "break-word" }} variant="body2">{`${folder_name}`}</Typography>
                    </Box>
                </Stack>
            }
            sx={isRootFolder ? {
                '.MuiTreeItem-iconContainer': {
                    width: 24
                },
                '.MuiTreeItem-iconContainer svg': {
                    fontSize: "24px !important",
                }
            } : {}}
            onClick={() => onFolderClicked(folder, parentFolder)}
        >
            {folder?.folders?.map(child => {
                return giveMeTreeItems(child, folder)
            })}

            {(foldersData.isLoading || foldersData.isFetching) && (currentItem?._id === folder._id) && <Box py={1}>
                <TreeItemLoader />
            </Box>}
        </TreeItem>
    };

    const massageFoldersForState = (folderToMassage: folderState[], parentIndices: number[]) => {
        return folderToMassage?.map((_, index) => ({ ..._, folders: [], index: [...parentIndices, index] })) || []
    };

    const fetchFolderById = async ({ folderId, index, sequence_id }: { folderId?: string, index: number[], sequence_id?: string }) => {

        const { data } = await fetchFolders({
            // object_id, object_type,
            parent_object_id: folderId ? folderId : object_id,
            parent_object_type: folderId ? 'folder' : object_type,
            sort_by: 'folder_name', sort_order: 'asc',
            parent_folder_id: folderId !== 'root' ? folderId : '',
            page_size: 10000, sequence_id
        });

        if (folders.length === 0 || index.length === 0) {
            if (sequence_id) {
                if (data?.data?.length) {
                    let newFolders: folderState[] = [
                        {
                            ...(data?.data?.[0] ?? {}),
                            index: [0],
                            folders: [],
                            ancestors: []
                        }
                    ];

                    setExpanded([newFolders[0]._id]);
                    setSelected(newFolders[0]._id);
                    setFolders(newFolders);
                    setCurrentItem(newFolders[0]);
                    setParentItem(undefined);
                }
            } else if (props.currentItem) {
                let newFolders: folderState[] = massageFoldersForState([{ ...props.currentItem, folders: massageFoldersForState(data?.data || [], []) }] as folderState[], []);

                if (newFolders?.length) {
                    setExpanded([newFolders[0]._id]);
                    setSelected(newFolders[0]._id);
                    setCurrentItem(newFolders[0]);
                    setFolders(newFolders);
                }

                setParentItem(undefined);
            } else {
                let newFolders: folderState[] = [
                    {
                        _id: 'root',
                        folder_name: ROOT_FOLDER_NAME,
                        index: [],
                        created_time: '',
                        created_user: '',
                        system_defined: true,
                        folders: [
                            ...massageFoldersForState(data?.data ?? [] as folderState[], [0])
                        ],
                        ancestors: []
                    }
                ];

                setSelected('root');
                setFolders(newFolders);
                setCurrentItem(newFolders[0]);
                setParentItem(undefined);
            }
        } else {
            addItem((data?.data || []) as folderState[], index);
        }
    };

    const updateFoldersRecursively = (folders: folderState[], newFolders: folderState[], index: number[], allIndex: number[]): folderState[] => {
        if (index.length === 1) {
            folders[index[0]].folders = massageFoldersForState(newFolders || [], allIndex);
        } else {
            folders[index[0]].folders = updateFoldersRecursively(folders[index[0]].folders, newFolders, index.slice(1, index.length), allIndex);
        }
        return folders;
    };

    const addItem = (childrenFolders: folderState[], index: number[]) => {
        let newFolders: folderState[] = copy(folders);

        // If the folder depth is 0
        if (index.length === 1) {
            newFolders[index[0]].folders = massageFoldersForState(childrenFolders, index);
        } else {
            newFolders[index[0]].folders = updateFoldersRecursively(newFolders[index[0]].folders, childrenFolders, index.slice(1, index.length), index);
        }

        setFolders([...newFolders]);
    };

    const showProperties = (folderDetails?: folderState) => {
        if (folderDetails) {
            dispatch(openDialog({
                title: "",
                hideNegativeBtn: true,
                hidePositiveBtn: true,
                enablePadding: false,
                body: <FolderProperties {...folderDetails} />
            }))
        }
    };

    const addEditFolder = (editFolder?: folderState) => {
        const isEdit = editFolder ? true : false;
        dispatch(openDialog({
            title: `${isEdit ? "Update" : "Create"} Folder`,
            hideCloseIcon: true,
            hideNegativeBtn: true,
            hidePositiveBtn: true,
            body: <AddEditFolder
                object_id={object_id}
                object_type={object_type}
                parentFolder={currentItem ? currentItem : undefined}
                folder={editFolder}
                isEdit={isEdit}
                history={history}
                refetch={(_state) => {
                    if (isEdit) {
                        fetchFolderById({ folderId: parentItem?._id, index: parentItem?.index ?? [] });

                        if (updateCurrentPath) {
                            updateCurrentPath(_state.folder_name ?? "");
                        }
                    } else {
                        let _id = currentItem ? currentItem._id !== 'root' ? currentItem._id : undefined : undefined;
                        let index = currentItem?.index ?? [];
                        fetchFolderById({ folderId: _id, index: index })
                        setExpanded((oldExpanded) => [...oldExpanded, `${_id}`]);
                    }
                }}
            />
        }))
    };

    const handleDeleteFolder = () => {
        dispatch(openConfirmationDialog({
            title: "Are you sure?",
            body: `Do you want to delete the folder: ${currentItem?.folder_name}?`,
            positiveBtn: 'Delete',
            negativeBtn: 'Cancel',
            onOk: () => confirmDeleteFolderDelete(false),
            onNegativeBtn: () => dispatch(closeConfirmationDialog())
        }))
    };

    const confirmDeleteFolderDelete = async (isForce: boolean) => {
        dispatch(openBackdrop('Deleting folder...'));

        await deleteFolder({ id: currentItem?._id, force: isForce }).then((res: any) => {

            if (res.error) {
                let errMessage = res.error?.data?.title ?? 'Something went wrong, Unable to delete folder.';
                enqueueSnackbar(errMessage, { variant: "error" });
                dispatch(updateCongirmationDialogBody(<Stack gap={2}>
                    <Typography gutterBottom variant="body1">{`Do you want to delete the folder: ${currentItem?.folder_name}?`}</Typography>
                    <Alert severity="warning">{errMessage}</Alert>
                    <Typography gutterBottom variant="body2" sx={{ mt: 2 }} color="error">Note: {`On Force deleting this folder, all the sub folders will be deleted and the documents will be visible in the ${ROOT_FOLDER_NAME} folder.`}</Typography>
                </Stack>));
                dispatch(updateConfirmationDialogPositiveBtn({
                    positiveBtn: "Yes, Force Delete Folder",
                    onOk: () => confirmDeleteFolderDelete(true)
                }))
            } else {
                if (parentItem) {
                    buildAndUpdateHistory(parentItem);
                }
                fetchFolderById({ folderId: (parentItem && parentItem._id !== 'root') ? parentItem._id : undefined, index: parentItem?.index ?? [] });
                enqueueSnackbar('Deleted the folder', { variant: "success" });
                dispatch(closeConfirmationDialog());
            }
        }).catch((err) => {
            enqueueSnackbar(err.data?.title ?? 'Something went wrong, Unable to delete folder.', { variant: "error" });
        });

        dispatch(closeBackdrop());
    };

    const onImportBtnClicked = async (template_id: string) => {
        dispatch(closeDialog());
        dispatch(openBackdrop('Importing from Folder Templates...'));
        await importFromFolderMutation({
            parent_object_id: object_id ? object_id : `${currentItem?._id} ?? ''`,
            parent_object_type: object_type ? object_type : 'folder',
            template_id,
            only_children: true
        }).then((res: any) => {
            if (res.data) {
                enqueueSnackbar('Folder Template Imported Successfully', { variant: "success" });
                fetchFolderById({ folderId: currentItem?._id, index: currentItem?.index ?? [] });
                setExpanded((oldExpanded) => [...oldExpanded, `${currentItem?._id ?? ""}`]);
            } else {
                enqueueSnackbar(res?.error?.data?.title ?? 'Something went wrong, Unable to import folder template.', { variant: "error" });
            }
        }).catch(err => {
            enqueueSnackbar(err?.data?.title ?? 'Something went wrong, Unable to import folder template.', { variant: "error" });
        }).finally(() => {
            dispatch(closeBackdrop());
        });
    };

    const importFromFolders = () => {
        dispatch(openDialog({
            title: "Import from Folder Templates",
            body: <ImportFromFolderTemplate onImportClicked={onImportBtnClicked} />,
            hideNegativeBtn: true,
            hidePositiveBtn: true,
        }))
    };

    React.useEffect(() => {
        fetchFolderById({ folderId: props?.currentItem?._id, index: props?.currentItem ? [0] : [], sequence_id: sequence_id });
        // eslint-disable-next-line
    }, []);

    React.useEffect(() => {
        setCurrentItem(props.currentItem ?? null);
        setFolders(props.currentItem ? [{ ...props.currentItem, index: [0], folders: [] }] : []);
        fetchFolderById({ folderId: props?.currentItem?._id, index: props?.currentItem ? [0] : [], sequence_id: sequence_id });
        if (props.currentItem) {
            setSelected(props.currentItem?._id)
        }
        // eslint-disable-next-line
    }, [props.currentItem]);

    React.useEffect(() => {
        if (!isMobileMatch) {
            dispatch(setOpenWebSideNavBar(false));
        }
        // eslint-disable-next-line
    }, []);

    const EndIcon: React.FC = () => <ChevronRight color="disabled" />;

    const FolderIcon = (folder: folderState) => {
        return folder?._id === "root" || folder.sequence_id === "F-000001" ? <InsertDriveFile htmlColor="primary" /> : <Folder htmlColor="#ffbf66" />
    };

    return <MyPaper height={"calc(100% - 6px)"} shadow={props.hideShadow ? 'unset' : ''} padding={0}>
        <>
            <Grid container sx={{ height: "100%" }}>

                {/* Folder Explorer Tree */}
                <Grid size={hideDocuments ? { xs: 12 } : { xs: 12, sm: 3, md: 3, lg: 3, xl: 3 }} sx={{
                    padding: "8px",
                    paddingRight: "1px",
                    height: "100%",
                    '--Grid-borderWidth': '1px',
                    borderRight: 'var(--Grid-borderWidth) solid',
                    borderColor: 'divider',
                    position: "relative",
                }}>

                    <Box sx={{
                        overflow: "scroll",
                        height: "100%"
                    }}>
                        {/* Folder Tree */}
                        <SimpleTreeView
                            slots={{ endIcon: EndIcon }}
                            expandedItems={expanded}
                            selectedItems={selected}
                            onExpandedItemsChange={handleToggle}
                            onSelectedItemsChange={(e, itemIds) => itemIds ? handleSelect(itemIds) : false}
                        >
                            {folders.map((folder) => giveMeTreeItems(folder))}
                        </SimpleTreeView>

                        {isCommonModule &&
                            <Stack
                                sx={(theme) => ({
                                    cursor: "pointer",
                                    width: "100%",
                                    mt: 1,
                                    px: 1,
                                    py: 1,
                                    borderRadius: 1,
                                    bgcolor: selected === trash._id
                                        ? theme.palette.primary.light
                                        : "transparent",
                                    "&:hover": {
                                        bgcolor: theme.palette.background.default,
                                    },
                                })}
                                onClick={() => {
                                    onFolderClicked(trash);
                                    setSelected(trash?._id);
                                }}
                                onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                        event.preventDefault();
                                        onFolderClicked(trash);
                                        setSelected(trash?._id);
                                    }
                                }}
                                role="treeitem"
                                tabIndex={0} // Makes it keyboard focusable
                                direction="row"
                                gap={1}
                                alignItems="center"
                            >
                                <Recycling htmlColor="#ffbf66" />
                                <Box>
                                    <Typography sx={{ wordBreak: "break-word" }} variant="body2">
                                        Trash
                                    </Typography>
                                </Box>
                            </Stack>
                        }

                        {/* Loading */}
                        {((foldersData.isLoading || foldersData.isFetching) && !currentItem) && <TreeItemLoader />}

                        {/* No Folders found */}
                        {!(foldersData.isLoading || foldersData.isFetching) && folders.length === 0 && <Stack sx={{ height: "100%", alignItems: "center", justifyContent: "center" }}>
                            <Typography variant="caption" sx={{ textAlign: "center" }}>You are yet to create a folder, please click the below button to create one.</Typography>
                        </Stack>}
                    </Box>

                    {/* Add Folder Button */}
                    {creatable && currentItem?._id !== "trash" &&
                        <Box sx={{ position: "absolute", bottom: "8px", right: "8px" }}>
                            <Stack onClick={parseInt(`${currentItem?.index?.length ?? 0}`) >= 10 ? () => { } : handleFolderClick} ref={menuRef}>
                                <Fab
                                    size="small"
                                    color="primary"
                                    variant={"circular"}
                                    disabled={parseInt(`${currentItem?.index?.length ?? 0}`) >= 10}
                                >
                                    <Add />
                                </Fab>
                            </Stack>

                            <Menu
                                id={folderActionsId}
                                open={openFolderActions}
                                anchorEl={anchorElForFolderActions}
                                onClose={handleFolderActionsClose}
                                anchorOrigin={{
                                    vertical: 'top',
                                    horizontal: 'left',
                                }}
                            >
                                {[
                                    { showIcon: true, hide: false, icon: <Add htmlColor="primary" />, name: "New Folder", onClick: () => addEditFolder() },
                                    { showIcon: true, hide: currentItem?.index?.length ?? true, icon: <SouthWest htmlColor="primary" />, name: "From Templates", onClick: () => importFromFolders() },
                                ]?.filter(option => !option.hide).map((option, index) => (<MenuItem key={index}
                                    sx={{ borderTop: (theme) => [1, 2].includes(index) ? `1px solid ${theme.palette.divider}` : `` }}
                                    onClick={() => {
                                        option.onClick();
                                        handleFolderActionsClose();
                                    }}>
                                    {option.showIcon && <ListItemIcon>{option.icon}</ListItemIcon>}
                                    <ListItemText>{option.name}</ListItemText>
                                </MenuItem>))}
                            </Menu>
                        </Box>
                    }
                </Grid>

                {!hideDocuments && <Grid size={{ xs: 12, sm: 9, md: 9, lg: 9, xl: 9 }} sx={{ height: "100%" }}>
                    {currentItem && <Documents
                        onRegenerate={props.onRegenerate}
                        onDocumentUploadComplete={props.onDocumentUploadComplete}
                        refetch={() => fetchFolderById({ folderId: currentItem?._id, index: currentItem?.index ?? [] })}
                        Id={`${object_sequence_id ?? ""}`}
                        _id={`${object_id ?? ""}`}
                        objectType={`${object_type ?? ""}`}
                        associations={object_association}
                        showCreateBtn={currentItem._id !== 'trash' && showCreateBtn}
                        showHelperBtn={showHelperBtn}
                        hideDeleteBtn={!showDeleteBtn}
                        creatable={currentItem._id !== 'trash' && creatable}
                        updatable={updatable}
                        publishable={publishable}
                        deletetable={deletable}
                        readable={readable}
                        folderId={(currentItem._id !== 'trash' && currentItem?._id !== "root") ? currentItem._id : ''}
                        unlinkDoc={unlinkDoc}
                        hasCheckbox={hasCheckbox}
                        linkDocument={linkDocument}
                        isPdfOnly={isPdfOnly}
                        parentData={parentData}
                        includeAssoc={currentItem._id === 'trash' ? ['none'] : []}
                    />}
                </Grid>}
            </Grid>

            <Menu
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
            >
                {[
                    { showIcon: true, hide: !creatable || parseInt(`${currentItem?.index?.length ?? 0}`) >= 10, icon: <Add color="primary" />, name: "New Folder", onClick: () => addEditFolder() },
                    { showIcon: true, hide: !updatable || currentItem?._id === 'root' || props.currentItem?._id === currentItem?._id, icon: <EditOutlined color="primary" />, name: "Edit", onClick: () => addEditFolder(currentItem ?? undefined) },
                    { showIcon: true, hide: !deletable || currentItem?._id === 'root' || props.currentItem?._id === currentItem?._id, icon: <DeleteOutline color="error" />, name: "Delete", onClick: handleDeleteFolder },
                    { showIcon: true, hide: currentItem?._id === 'root', icon: <InfoOutlined fontSize="small" color="primary" />, name: "Properties", onClick: () => showProperties(currentItem ?? undefined) },
                ]?.filter(opt => opt.hide === false).map((option, index) => (<MenuItem key={index}
                    sx={{ borderTop: (theme) => [1, 2, 3].includes(index) ? `1px solid ${theme.palette.divider}` : `` }}
                    onClick={() => {
                        option.onClick();
                        setAnchorEl(null);
                    }}>
                    {option.showIcon && <ListItemIcon>{option.icon}</ListItemIcon>}
                    <ListItemText>{option.name}</ListItemText>
                </MenuItem>))}
            </Menu>
        </>
    </MyPaper>
}